import { useState } from "react";
import { X, Download, Send, Loader } from "lucide-react";
import axios from "axios";
import { useLanguage } from "../contexts/LanguageContext";

import { API } from "../config";

export default function QuoteModal({ items, total, projectName, onClose }) {
  const { t } = useLanguage();
  const [form, setForm] = useState({ customer_name: "", customer_email: "", customer_phone: "", notes: "" });
  const [loading, setLoading] = useState(null);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const payload = () => ({
    customer_name: form.customer_name,
    customer_email: form.customer_email,
    customer_phone: form.customer_phone,
    project_name: projectName || "Renovācijas projekts",
    items: items.map((i) => ({
      name: i.name,
      quantity: i.quantity,
      unit: i.unit,
      price: i.price,
      total: i.quantity * i.price,
    })),
    total_cost: total,
    notes: form.notes,
  });

  const handleEmail = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_email) return;
    setLoading("email");
    setError("");
    try {
      await axios.post(`${API}/quote/email`, payload());
      setSuccess(t("quote.success"));
    } catch (err) {
      setError(err?.response?.data?.detail || "Kļūda nosūtot e-pastu");
    } finally {
      setLoading(null);
    }
  };

  const handlePdf = async () => {
    if (!form.customer_name) { setError("Ievadiet vārdu PDF generēšanai"); return; }
    setLoading("pdf");
    setError("");
    try {
      const res = await axios.post(`${API}/quote/pdf`, payload(), { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `RMD_taame_${(projectName || "projekts").replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError("PDF ģenerēšanas kļūda");
    } finally {
      setLoading(null);
    }
  };

  return (
    <div
      data-testid="quote-modal"
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 px-4 py-8"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="bg-white border-2 border-black w-full max-w-lg max-h-[90vh] overflow-y-auto"
        style={{ boxShadow: "6px 6px 0 #000" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b-2 border-black bg-[#FF6600] sticky top-0 z-10">
          <div>
            <h2 className="text-lg font-black uppercase tracking-tight text-black" style={{ fontFamily: "Chivo, sans-serif" }}>
              {t("quote.title")}
            </h2>
            <p className="text-black/70 text-xs mt-0.5">{t("quote.subtitle")}</p>
          </div>
          <button data-testid="quote-modal-close" onClick={onClose}>
            <X size={22} />
          </button>
        </div>

        <div className="p-6">
          {/* Project summary */}
          <div className="mb-5 p-4 bg-gray-50 border border-gray-200">
            <div className="flex justify-between items-center">
              <span className="text-sm font-semibold">{projectName || "Renovācijas projekts"}</span>
              <span className="font-black text-lg text-[#FF6600]" style={{ fontFamily: "Chivo, sans-serif" }}>€{total.toFixed(2)}</span>
            </div>
            <div className="text-xs text-gray-500 mt-1">{items.length} pozīcij{items.length === 1 ? "a" : "as"}</div>
          </div>

          <form onSubmit={handleEmail} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="rmd-label block mb-1">{t("quote.name")} *</label>
                <input
                  data-testid="quote-name-input"
                  type="text"
                  required
                  value={form.customer_name}
                  onChange={(e) => set("customer_name", e.target.value)}
                  placeholder="Jānis Bērziņš"
                  className="w-full border-2 border-black px-3 py-2 text-sm focus:outline-none focus:border-[#FF6600]"
                />
              </div>
              <div>
                <label className="rmd-label block mb-1">{t("quote.phone")}</label>
                <input
                  data-testid="quote-phone-input"
                  type="tel"
                  value={form.customer_phone}
                  onChange={(e) => set("customer_phone", e.target.value)}
                  placeholder="+371 XXXXXXXX"
                  className="w-full border-2 border-black px-3 py-2 text-sm focus:outline-none focus:border-[#FF6600]"
                />
              </div>
            </div>

            <div>
              <label className="rmd-label block mb-1">{t("quote.email")} *</label>
              <input
                data-testid="quote-email-input"
                type="email"
                required
                value={form.customer_email}
                onChange={(e) => set("customer_email", e.target.value)}
                placeholder="janis@epasts.lv"
                className="w-full border-2 border-black px-3 py-2 text-sm focus:outline-none focus:border-[#FF6600]"
              />
            </div>

            <div>
              <label className="rmd-label block mb-1">{t("quote.notes")}</label>
              <textarea
                data-testid="quote-notes-input"
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                placeholder={t("quote.notesPlaceholder")}
                rows={3}
                className="w-full border-2 border-black px-3 py-2 text-sm focus:outline-none focus:border-[#FF6600] resize-none"
              />
            </div>

            {error && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 border border-red-200">{error}</div>}
            {success && <div className="text-sm text-green-600 bg-green-50 px-3 py-2 border border-green-200">{success}</div>}

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                type="button"
                data-testid="quote-pdf-btn"
                onClick={handlePdf}
                disabled={!!loading}
                className="rmd-btn-secondary flex-1 justify-center text-sm"
              >
                {loading === "pdf" ? <Loader size={16} className="animate-spin" /> : <Download size={16} />}
                {t("quote.download")}
              </button>
              <button
                type="submit"
                data-testid="quote-email-btn"
                disabled={!!loading}
                className="rmd-btn-primary flex-1 justify-center text-sm"
              >
                {loading === "email" ? <Loader size={16} className="animate-spin" /> : <Send size={16} />}
                {t("quote.send")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
